import {
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown } from 'lucide-react';
import { CHIP_TRIGGER_CLASS } from './FilterChips';

type Option<T extends string | number> = {
  value: T;
  label: string;
  /** Seção do painel. Opções do mesmo grupo devem vir juntas; o cabeçalho
   * sai antes da primeira de cada grupo. */
  group?: string;
  /** Texto do chip quando selecionada (padrão: `label`) — ex.: com grupos,
   * para o chip dizer de qual seção veio. */
  chipLabel?: string;
};

interface Props<T extends string | number> {
  options: Option<T>[];
  value: T[];
  onChange: (next: T[]) => void;
  placeholder: string;
  disabled?: boolean;
  emptyHint?: string;
  /**
   * Gatilho em forma de chip de filtro ("Empresa: Todas ▾"), com este rótulo
   * antes do valor; `placeholder` vira o valor quando nada está escolhido.
   * Sem ele, o gatilho é o campo largo com os chips das escolhas.
   */
  chipLabel?: string;
}

export function MultiSelect<T extends string | number>({
  options, value, onChange, placeholder, disabled, emptyHint, chipLabel,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  /**
   * Posição do painel na viewport. Ele é renderizado em `document.body` por
   * portal, não dentro do componente: em modal (ou qualquer pai com `overflow`)
   * o dropdown absoluto era CORTADO pela borda do container, e as últimas
   * opções ficavam inalcançáveis. Fora da árvore, nenhum `overflow` o clipa.
   */
  const [pos, setPos] = useState<{
    left: number;
    width: number;
    top?: number;
    bottom?: number;
    maxHeight: number;
  } | null>(null);

  const medir = useCallback(() => {
    const b = ref.current?.getBoundingClientRect();
    if (!b) return;

    const MARGEM = 8;
    const abaixo = window.innerHeight - b.bottom - MARGEM;
    const acima = b.top - MARGEM;
    // Abre para cima quando não cabe embaixo e há mais espaço em cima.
    const paraCima = abaixo < 200 && acima > abaixo;

    // O chip é estreito: o painel tem largura mínima para as opções caberem.
    // O campo normal (largo) segue a largura dele, como antes.
    const width = chipLabel ? Math.max(b.width, 256) : b.width;
    setPos({
      left: Math.max(MARGEM, Math.min(b.left, window.innerWidth - width - MARGEM)),
      width,
      ...(paraCima
        ? { bottom: window.innerHeight - b.top + 4 }
        : { top: b.bottom + 4 }),
      maxHeight: Math.min(288, paraCima ? acima : abaixo),
    });
  }, [chipLabel]);

  // Reposiciona enquanto aberto: `fixed` não acompanha scroll do pai, e o
  // `capture` pega o scroll de qualquer container, não só o da janela.
  // `useLayoutEffect` para medir ANTES do paint: com `useEffect` o primeiro
  // frame saía sem `left/top/width`, e o painel piscava em posição estática com
  // altura livre.
  useLayoutEffect(() => {
    if (!open) return;
    medir();
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [open, medir]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selected = useMemo(
    () => options.filter((o) => selectedSet.has(o.value)),
    [options, selectedSet],
  );
  const MAX_CHIPS = 2;
  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const toggle = (v: T) => {
    onChange(selectedSet.has(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  const chipValue =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? (selected[0].chipLabel ?? selected[0].label)
        : `${selected.length} selecionados`;

  return (
    <div ref={ref} className="relative">
      {chipLabel ? (
        // Sem `aria-label`: o nome acessível é o próprio texto do chip.
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={`${CHIP_TRIGGER_CLASS} disabled:opacity-50`}
        >
          <span className="max-w-[16rem] truncate">
            {/* Sem `placeholder` (ex.: "Etapa"), nada escolhido mostra só o rótulo. */}
            {chipValue ? `${chipLabel}: ${chipValue}` : chipLabel}
          </span>
          <ChevronDown size={14} className="shrink-0 text-subtle" />
        </button>
      ) : (
      <button
        type="button"
        disabled={disabled}
        aria-label="open"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-8 px-2.5 py-1 border border-border-subtle rounded-md bg-surface text-body text-left text-[13px] flex items-center gap-1 flex-nowrap overflow-hidden transition-colors hover:border-border-strong focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {value.length === 0 ? (
          <span className="text-muted">{placeholder}</span>
        ) : (
          <>
            {selected.slice(0, MAX_CHIPS).map((o) => (
              <span key={String(o.value)} className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-[12px] font-medium rounded inline-flex items-center gap-1 min-w-0">
                <span className="truncate">{o.chipLabel ?? o.label}</span>
                {!disabled && (
                  <X
                    size={12}
                    className="cursor-pointer shrink-0"
                    onClick={(e) => { e.stopPropagation(); toggle(o.value); }}
                  />
                )}
              </span>
            ))}
            {selected.length > MAX_CHIPS && (
              <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-[12px] font-medium rounded shrink-0">
                +{selected.length - MAX_CHIPS}
              </span>
            )}
          </>
        )}
        <ChevronDown size={14} className="ml-auto shrink-0 text-subtle" />
      </button>
      )}

      {open && !disabled && createPortal(
        <>
          {/*
            Clique fora fecha SÓ o dropdown. Um listener no `document` fechava o
            dropdown no mousedown e, com o painel já desmontado, o `click`
            seguinte caía no que estava atrás — dentro de um modal, no overlay,
            que fechava o modal junto. Esta camada recebe o próprio clique e o
            interrompe aqui: um clique fecha o dropdown, o próximo é que alcança
            o que está atrás.
          */}
          <div
            role="presentation"
            className="fixed inset-0 z-[199]"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            style={{
              position: 'fixed',
              left: pos?.left,
              width: pos?.width,
              top: pos?.top,
              bottom: pos?.bottom,
              maxHeight: pos?.maxHeight,
            }}
            className="z-[200] bg-surface border border-border-subtle rounded-lg shadow-xl ring-1 ring-black/5 overflow-y-auto scrollbar-hide"
          >
            <div className="sticky top-0 z-10 p-1.5 bg-surface border-b border-border-subtle">
              <div className="relative">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-subtle" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-surface-muted text-body placeholder-subtle w-full pl-7 pr-2 py-1 text-[13px] border border-border-subtle rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition-colors"
                  placeholder="Buscar..."
                />
              </div>
            </div>
            <div className="p-1">
            {filtered.length === 0 ? (
              <div className="px-2.5 py-3 text-[13px] text-muted">{emptyHint || 'Nada encontrado'}</div>
            ) : (
              filtered.map((o, i) => (
                <div key={String(o.value)}>
                {o.group && o.group !== filtered[i - 1]?.group && (
                  <div className="px-2 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-subtle">
                    {o.group}
                  </div>
                )}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(o.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(o.value);
                    }
                  }}
                  className={`px-2 py-1 rounded-md text-[13px] cursor-pointer transition-colors flex items-center gap-2 ${selectedSet.has(o.value) ? 'bg-blue-50 dark:bg-blue-900/25 text-blue-800 dark:text-blue-200 font-medium' : 'text-body/90 hover:bg-surface-muted'}`}
                >
                  <input type="checkbox" readOnly checked={selectedSet.has(o.value)} className="shrink-0 h-3.5 w-3.5 rounded accent-blue-600 dark:accent-blue-400" />
                  <span className="truncate">{o.label}</span>
                </div>
                </div>
              ))
            )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
